import type { ButtonHTMLAttributes, ReactNode } from 'react';

/** Visual intent of a {@link Button}. */
export type ButtonVariant = 'primary' | 'secondary' | 'danger';

/** Physical size of a {@link Button}. */
export type ButtonSize = 'md' | 'icon';

/** Props for {@link Button}. Extends native button attributes. */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual intent. `danger` maps to the brand accent red (destructive only). */
  variant?: ButtonVariant;
  /**
   * Physical size. `md` is the default text button; `icon` is a compact square
   * control for a single glyph (e.g. a row's Edit/Delete action). An `icon`
   * button carries no visible text, so callers MUST pass an `aria-label`.
   */
  size?: ButtonSize;
  /** Button label / content. */
  children: ReactNode;
}

/**
 * Per-variant Tailwind classes.
 *
 * Why these choices:
 * - `primary` uses the brand blue — the single dominant call to action.
 * - `secondary` is a low-emphasis outline that reads correctly in both themes.
 * - `danger` uses the accent red, reserved for destructive/critical actions
 *   (never decorative), per the brand system.
 */
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 ' +
    'focus-visible:outline-brand-500 disabled:hover:bg-brand-500',
  secondary:
    'bg-transparent text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-100 ' +
    'active:bg-slate-200 focus-visible:outline-slate-400 ' +
    'dark:text-slate-100 dark:ring-navy-border dark:hover:bg-navy-surface dark:active:bg-navy-bg',
  danger:
    'bg-accent-500 text-white hover:bg-accent-600 active:bg-accent-700 ' +
    'focus-visible:outline-accent-500 disabled:hover:bg-accent-500',
};

/**
 * Primary interactive control for the design system.
 *
 * Accessibility:
 * - Renders a real `<button>` (keyboard + screen-reader native).
 * - Visible focus ring via `focus-visible:outline-*` (never removes focus).
 * - Disabled state lowers opacity and blocks the pointer.
 *
 * The component is intentionally minimal for Phase 0; Phase 1 can grow sizes,
 * loading states and icon slots on top of this stable base.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  type = 'button',
  children,
  ...props
}: ButtonProps): JSX.Element {
  // `icon` drops the horizontal padding to a square footprint so a single glyph
  // sits centred; `md` keeps the standard text-button padding.
  const sizeClass = size === 'icon' ? 'p-2' : 'px-4 py-2';
  const base =
    'inline-flex items-center justify-center gap-2 rounded-md ' +
    `${sizeClass} ` +
    'text-sm font-medium transition-colors ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
    'disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <button
      type={type}
      className={`${base} ${VARIANT_CLASSES[variant]} ${className}`.trim()}
      {...props}
    >
      {children}
    </button>
  );
}
